import { getActiveSessionManager as SessionManager, createSessionWithSignature, genAuthNonce, genSolanaMessage, } from '@bounded-sh/core';
import { getPlatform } from '../../platform';
import { setCurrentUser, setAuthLoading } from '../../global';
import { getGuestKey, getExistingGuestKey, clearGuestKey } from './guest-keystore';
const GUEST_TX_UNSUPPORTED = 'Guest (anonymous) auth is offchain-only — it cannot sign or submit Solana transactions. ' +
    'Use a wallet provider for onchain actions.';
export class GuestAuthProvider {
    async loginInternal(commitGlobalState, manageLoading) {
        if (manageLoading)
            setAuthLoading(true);
        try {
            const key = await getGuestKey();
            // Reuse a still-valid session for this exact guest.
            const existing = await SessionManager().getSession();
            if (existing && existing.address === key.address) {
                if (!this.tokenIsGuest(SessionManager().getIdToken(), key.address)) {
                    throw new Error('The active session for this device key is not anonymous. Sign out explicitly before starting a guest session.');
                }
                const user = { address: key.address, isAnonymous: true, provider: this };
                if (commitGlobalState)
                    setCurrentUser(user);
                return user;
            }
            // Mint a fresh session: nonce -> canonical message -> local signature.
            const nonce = await genAuthNonce();
            const message = await genSolanaMessage(key.address, nonce);
            const signature = await key.sign(message); // base64 detached ed25519
            // Pass the discriminator explicitly. During a transactional switch the
            // shared core config intentionally remains on the previous provider until
            // this session is safely minted and persisted.
            const result = await createSessionWithSignature(key.address, message, signature, 'guest');
            if (!this.tokenIsGuest(result.idToken, key.address)) {
                throw new Error('Guest session issuer returned a non-anonymous or identity-mismatched token.');
            }
            await SessionManager().storeSession(key.address, result.accessToken, result.idToken, result.refreshToken);
            const user = { address: key.address, isAnonymous: true, provider: this };
            if (commitGlobalState)
                setCurrentUser(user);
            return user;
        }
        finally {
            if (manageLoading)
                setAuthLoading(false);
        }
    }
    async login() {
        return this.loginInternal(true, true);
    }
    /** @internal Stage a guest session without exposing mixed provider/user state. */
    async loginForProviderSwitch() {
        return this.loginInternal(false, false);
    }
    tokenIsGuest(idToken, expectedAddress) {
        var _a;
        try {
            if (!idToken)
                return false;
            let b64 = (_a = idToken.split('.')[1]) === null || _a === void 0 ? void 0 : _a.replace(/-/g, '+').replace(/_/g, '/');
            if (!b64)
                return false;
            const pad = b64.length % 4;
            if (pad === 2)
                b64 += '==';
            else if (pad === 3)
                b64 += '=';
            const claims = JSON.parse(getPlatform().atob(b64));
            if ((claims === null || claims === void 0 ? void 0 : claims.is_anonymous) !== true)
                return false;
            if (expectedAddress && (claims === null || claims === void 0 ? void 0 : claims['custom:walletAddress']) !== expectedAddress)
                return false;
            return true;
        }
        catch (_b) {
            return false;
        }
    }
    async restoreSessionInternal(commitGlobalState) {
        const manager = SessionManager();
        const session = await manager.getSession();
        if (!session)
            return null;
        // The marker/provider choice is not authority. A guest provider may only
        // continue with an issuer-stamped guest bearer whose persisted device key
        // still controls the exact same address. Anything else is an identity split
        // and must lose bearer authority immediately.
        if (!this.tokenIsGuest(manager.getIdToken(), session.address)) {
            await manager.clearSession();
            return null;
        }
        let key;
        try {
            key = await getExistingGuestKey();
        }
        catch (_a) {
            await manager.clearSession();
            return null;
        }
        if (!key || session.address !== key.address) {
            await manager.clearSession();
            return null;
        }
        const user = { address: key.address, isAnonymous: true, provider: this };
        if (commitGlobalState)
            setCurrentUser(user);
        return user;
    }
    async restoreSession() {
        return this.restoreSessionInternal(true);
    }
    /** @internal Reconcile an applied-yet-error guest mint before committing a switch. */
    async restoreSessionForProviderSwitch() {
        return this.restoreSessionInternal(false);
    }
    async logout() {
        // Clears the SESSION but keeps the device key, so logging back in returns the
        // SAME guest identity (and its data). Use forgetGuest() to wipe the key too.
        await SessionManager().clearSession();
        setCurrentUser(null);
    }
    /** Wipe the device key — the next login() is a brand-new guest identity. */
    async forgetGuest() {
        await SessionManager().clearSession();
        await clearGuestKey();
        setCurrentUser(null);
    }
    async signMessage(message) {
        const key = await getGuestKey();
        return key.sign(message);
    }
    async signTransaction(_t) {
        throw new Error(GUEST_TX_UNSUPPORTED);
    }
    async signAndSubmitTransaction() {
        throw new Error(GUEST_TX_UNSUPPORTED);
    }
    async getNativeMethods() {
        return {};
    }
}
